
import { format } from "date-fns";
import { cmToInches, gToLb } from "@/utils/familyProfile";

// Cache for parsed values
const heightCache = new Map<string, number | null>();
const weightCache = new Map<string, number | null>();

// Helper to convert height/weight strings to numeric values with caching
export const parseHeight = (height: string | undefined): number | null => {
  if (!height) return null;
  
  // Check cache first
  if (heightCache.has(height)) {
    return heightCache.get(height) || null;
  }
  
  // Handle different formats: "50 cm", "50cm", just "50"
  const match = height.match(/(\d+\.?\d*)/);
  const value = match ? parseFloat(match[1]) : null;
  
  // Store in cache
  heightCache.set(height, value);
  
  return value;
};

export const parseWeight = (weight: string | undefined): number | null => {
  if (!weight) return null;
  
  // Check cache first
  if (weightCache.has(weight)) {
    return weightCache.get(weight) || null;
  }
  
  // Handle different formats: "3500 g", "3500g", just "3500"
  const match = weight.match(/(\d+\.?\d*)/);
  const value = match ? parseFloat(match[1]) : null;
  
  // Store in cache
  weightCache.set(weight, value);
  
  return value;
};

// Prepare data for the height chart with optimized processing
export const getHeightData = (filteredEntries: any[], useMetricHeight: boolean) => {
  const data = filteredEntries
    .filter(entry => entry.height) // Only entries with height
    .map(entry => {
      let heightValue = parseHeight(entry.height);
      
      return {
        date: new Date(entry.date),
        formattedDate: format(new Date(entry.date), "MMM d, yyyy"),
        height: heightValue,
        displayHeight: heightValue ? (useMetricHeight ? heightValue : cmToInches(heightValue)) : null,
        age: entry.age
      };
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime()); // Sort by date ascending
    
  return data;
};

// Prepare data for the weight chart with optimized processing
export const getWeightData = (filteredEntries: any[], useMetricWeight: boolean) => {
  const data = filteredEntries
    .filter(entry => entry.weight) // Only entries with weight
    .map(entry => {
      let weightValue = parseWeight(entry.weight);
      
      return {
        date: new Date(entry.date),
        formattedDate: format(new Date(entry.date), "MMM d, yyyy"),
        weight: weightValue,
        displayWeight: weightValue ? (useMetricWeight ? weightValue : gToLb(weightValue)) : null,
        age: entry.age
      };
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime()); // Sort by date ascending
    
  return data;
};

// Cache for average data calculations
const averageDataCache = new Map<string, any[]>();

// Prepare average data with caching
export function prepareAverageData(
  allDataPoints: any[],
  showAverageLine: boolean,
  getAverageValueForAge: (ageMonths: number) => number,
  valueKey: string,
  babyAgeInMonths: number
) {
  // If not showing average line, return empty array
  if (!showAverageLine) return [];
  
  // Generate cache key
  const cacheKey = `${valueKey}-${babyAgeInMonths}-${showAverageLine}`;
  
  // Check if we have cached data
  if (averageDataCache.has(cacheKey)) {
    return averageDataCache.get(cacheKey) || [];
  }
  
  // Generate average values for months 0 to max of 12 or baby age + 3
  const maxMonth = Math.max(12, babyAgeInMonths + 3);
  const averageData = [];
  
  for (let month = 0; month <= maxMonth; month++) {
    const averageValue = getAverageValueForAge(month);
    averageData.push({
      month,
      formattedDate: `Month ${month}`,
      averageValue
    });
  }
  
  console.log(`[GrowthDataUtils] Generated average values for months 0-${maxMonth}: ${averageData.length}`);
  
  // Store in cache
  averageDataCache.set(cacheKey, averageData);
  
  return averageData;
}

// Function to calculate y-axis domain with caching
const domainCache = new Map<string, [number, number]>();

export function calculateYAxisDomain(
  dataPoints: any[],
  averageData: any[],
  showAverageLine: boolean,
  valueKey: string
): [number, number] {
  // Generate cache key
  const dataHash = dataPoints.length + (showAverageLine ? averageData.length : 0);
  const cacheKey = `${valueKey}-${dataHash}-${showAverageLine}`;
  
  // Check if we have cached domain
  if (domainCache.has(cacheKey)) {
    return domainCache.get(cacheKey) as [number, number];
  }
  
  // Extract all values
  const values: number[] = dataPoints
    .map(point => point[valueKey])
    .filter((value): value is number => typeof value === 'number');
  
  // Add average values if showing average line
  if (showAverageLine && averageData.length > 0) {
    const averageValues = averageData
      .map(point => point.averageValue)
      .filter((value): value is number => typeof value === 'number');
    values.push(...averageValues);
  }
  
  // Handle empty values
  if (values.length === 0) {
    return ['auto', 'auto'] as any;
  }
  
  // Calculate min and max with padding
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = (max - min) * 0.1; // 10% padding
  
  const domain: [number, number] = [
    Math.max(0, min - padding), // Never go below zero
    max + padding
  ];
  
  console.log(`[GrowthDataUtils] Calculated y-axis domain: [${domain[0]}, ${domain[1]}] from ${values.length} values`);
  
  // Store in cache
  domainCache.set(cacheKey, domain);
  
  return domain;
}
